import { useState, useRef } from "react";
import "./textToSpeech.scss";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import ReplayIcon from "@mui/icons-material/Replay";
import ReactionBoard from "./ReactionBoard/ReactionBoard";
import StoredTexts from "./StoredTexts/StoredTexts";
import { addHistoryItem, db } from "../../services/db";
import { useLiveQuery } from "dexie-react-hooks";
import getRelevantTexts from "../../utilities/getRelevantTexts";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { speakText } from "../../services/polly";
import CancelIcon from "@mui/icons-material/Cancel";
import { HotKeys, ObserveKeys } from "react-hotkeys";
import alarmSound from "../../assets/alarm.mp3";

type TextToSpeechProps = {
	selectedTag: string | null;
	setSelectedTag: React.Dispatch<React.SetStateAction<string | null>>;
};

const TextToSpeech = ({ selectedTag, setSelectedTag }: TextToSpeechProps) => {
	const [text, setText] = useState("");
	const [headline, setHeadline] = useState<string | null>(null);
	// determines whether a shortcut button is use to speak an already saved text or save a new one
	const [buttonMode, setButtonMode] = useState<"speak" | "save">("speak");

	const storedTexts = useLiveQuery(() => db.texts.toArray());
	const relevantTexts = getRelevantTexts(storedTexts, selectedTag);

	const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
	const alarmAudioRef = useRef<HTMLAudioElement | null>(null);

	const handleChange = (e: any) => {
		const [text, headline] = e.target.value.split("$").reverse();
		setText(text);
		setHeadline(headline);
	};

	const handlePlayback = () => {
		speakText(text);
		addHistoryItem(text);
	};

	const handleAlarm = () => {
		if (!alarmAudioRef.current) {
			alarmAudioRef.current = new Audio(alarmSound);
		}

		if (isAlarmPlaying) {
			// Stop the alarm
			alarmAudioRef.current.pause();
			alarmAudioRef.current.currentTime = 0; // Reset playback position
			setIsAlarmPlaying(false);
		} else {
			// Play the alarm
			alarmAudioRef.current.play();
			setIsAlarmPlaying(true);
		}
	};

	const handleAddText = () => (buttonMode === "speak" ? setButtonMode("save") : setButtonMode("speak"));
	const handleDeleteText = () => setText("");

	const keyMap = {
		PLAY: "ctrl+q",
		DELETE: "ctrl+ü",
	};

	const handlers = {
		PLAY: handlePlayback,
		DELETE: handleDeleteText,
	};

	return (
		<>
			<div className="tts-area-wrapper">
				<HotKeys keyMap={keyMap} handlers={handlers}>
					<ObserveKeys>
						<textarea
							className="tts-area"
							onChange={handleChange}
							aria-label="text-to-speech text field"
							value={text}
						/>
					</ObserveKeys>
				</HotKeys>
			</div>
			<div className="shortcuts">
				<span className="shortcut">ctrl+q to play</span>
				<span className="shortcut">ctrl+ü to delete</span>
				<span className="shortcut">$ to create headline</span>
			</div>
			<div className="playButtonWrapper">
				<PlayCircleFilledWhiteIcon className="playButton" fontSize="inherit" onClick={handlePlayback} />
				<ReplayIcon fontSize="inherit" />
				<AddCircleIcon fontSize="inherit" onClick={handleAddText} />
				<CancelIcon fontSize="inherit" onClick={handleDeleteText} />
			</div>
			{relevantTexts?.length ? (
				<StoredTexts texts={relevantTexts} textSetter={{ setText, setHeadline }} />
			) : (
				<ReactionBoard
					headline={headline}
					text={text}
					setSelectedTag={setSelectedTag}
					storedTexts={storedTexts}
					buttonMode={buttonMode}
				/>
			)}
			<div className="big-red-button-wrapper">
				<button className="big-red-button" onClick={handleAlarm}>
					{isAlarmPlaying ? "Pause Alarm" : "Play Alarm"}
				</button>
			</div>
		</>
	);
};

export default TextToSpeech;
